/**
 * Toast Notification Models
 *
 * Defines the data structures and types for toast notifications
 * including message types, display options, and queue management.
 */

/**
 * Supported toast notification types
 */
export type ToastType = 'success' | 'warning' | 'error' | 'info';

/**
 * Toast notification message interface
 */
export interface ToastMessage {
  /** Unique identifier for the toast */
  id: string;
  /** The type of toast notification */
  type: ToastType;
  /** The main message text */
  message: string;
  /** Optional detailed description */
  description?: string;
  /** Duration in milliseconds to show the toast (0 = persistent) */
  duration?: number;
  /** Whether the toast can be manually dismissed */
  dismissible?: boolean;
  /** Custom action button text */
  actionText?: string;
  /** Callback function for action button */
  onAction?: () => void;
  /** Timestamp when the toast was created */
  timestamp: number;
}

/**
 * Toast display options for creating new toasts
 */
export interface ToastOptions {
  /** Duration in milliseconds (default: 5000ms for success/warning, 0 for error) */
  duration?: number;
  /** Whether the toast can be manually dismissed (default: true) */
  dismissible?: boolean;
  /** Optional detailed description */
  description?: string;
  /** Custom action button text */
  actionText?: string;
  /** Callback function for action button */
  onAction?: () => void;
}

/**
 * Toast queue configuration
 */
export interface ToastQueueConfig {
  /** Maximum number of toasts to show simultaneously */
  maxVisible: number;
  /** Maximum number of toasts to keep in queue */
  maxQueueSize: number;
  /** Position of toasts on screen */
  position: ToastPosition;
}

/**
 * Supported toast positions
 */
export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

/**
 * Toast queue state
 */
export interface ToastQueueState {
  /** Currently visible toasts */
  visible: ToastMessage[];
  /** Queued toasts waiting to be shown */
  queued: ToastMessage[];
  /** Configuration settings */
  config: ToastQueueConfig;
}

/**
 * Toast service event types
 */
export type ToastEventType = 'add' | 'remove' | 'clear' | 'update';

/**
 * Toast service event data
 */
export interface ToastEvent {
  type: ToastEventType;
  toast?: ToastMessage;
  toastId?: string;
}

/**
 * Default toast configurations by type
 */
export const DEFAULT_TOAST_CONFIG: Record<ToastType, Partial<ToastOptions>> = {
  success: {
    duration: 2000,
    dismissible: true
  },
  warning: {
    duration: 2000,
    dismissible: true
  },
  error: {
    duration: 0, // Persistent until dismissed
    dismissible: true
  },
  info: {
    duration: 2000,
    dismissible: true
  }
};

/**
 * Default queue configuration
 */
export const DEFAULT_QUEUE_CONFIG: ToastQueueConfig = {
  maxVisible: 5,
  maxQueueSize: 20,
  position: 'top-right'
};